import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getCanonicalUrl,
  getSiteUrl,
  getSocialImageUrl,
  pageMetadata,
  siteMetadata,
} from '../config/siteMetadata';

function setMetaAttribute(attribute, key, content) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
}

function setLinkRel(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
}

function setJsonLd(id, data) {
  let element = document.getElementById(id);

  if (!element) {
    element = document.createElement('script');
    element.id = id;
    element.type = 'application/ld+json';
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(data);
}

function buildStructuredData(canonicalUrl) {
  const siteUrl = getSiteUrl();
  const sameAs = [
    siteMetadata.alternateDomain,
    ...siteMetadata.socialProfiles,
  ];

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${siteUrl}/#website`,
        url: siteUrl,
        name: siteMetadata.name,
        alternateName: siteMetadata.shortName,
        inLanguage: 'en-US',
        publisher: {
          '@id': `${siteUrl}/#organization`,
        },
      },
      {
        '@type': ['Organization', 'Place'],
        '@id': `${siteUrl}/#organization`,
        name: siteMetadata.name,
        alternateName: siteMetadata.shortName,
        url: siteUrl,
        email: siteMetadata.email,
        image: getSocialImageUrl(),
        sameAs,
        address: {
          '@type': 'PostalAddress',
          ...siteMetadata.address,
        },
        geo: {
          '@type': 'GeoCoordinates',
          ...siteMetadata.geo,
        },
      },
      {
        '@type': 'WebPage',
        '@id': `${canonicalUrl}#webpage`,
        url: canonicalUrl,
        name: document.title,
        isPartOf: {
          '@id': `${siteUrl}/#website`,
        },
        about: {
          '@id': `${siteUrl}/#organization`,
        },
        inLanguage: 'en-US',
      },
    ],
  };
}

function SEO() {
  const { pathname } = useLocation();

  useEffect(() => {
    const normalizedPath = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
    const metadata = pageMetadata[normalizedPath] || {
      title: `Page Not Found | ${siteMetadata.name}`,
      description: `The requested page could not be found on ${siteMetadata.name}.`,
      noIndex: true,
    };
    const canonicalUrl = getCanonicalUrl(pageMetadata[normalizedPath] ? normalizedPath : '/');
    const socialImageUrl = getSocialImageUrl();

    document.title = metadata.title;

    setMetaAttribute('name', 'description', metadata.description);
    setMetaAttribute(
      'name',
      'robots',
      metadata.noIndex ? 'noindex,follow' : 'index,follow'
    );

    setMetaAttribute('property', 'og:site_name', siteMetadata.name);
    setMetaAttribute('property', 'og:title', metadata.title);
    setMetaAttribute('property', 'og:description', metadata.description);
    setMetaAttribute('property', 'og:type', 'website');
    setMetaAttribute('property', 'og:url', canonicalUrl);
    setMetaAttribute('property', 'og:image', socialImageUrl);
    setMetaAttribute('property', 'og:image:alt', `${siteMetadata.name} in Philadelphia`);
    setMetaAttribute('property', 'og:image:width', siteMetadata.imageWidth);
    setMetaAttribute('property', 'og:image:height', siteMetadata.imageHeight);
    setMetaAttribute('property', 'og:locale', 'en_US');

    setMetaAttribute('name', 'twitter:card', 'summary_large_image');
    setMetaAttribute('name', 'twitter:title', metadata.title);
    setMetaAttribute('name', 'twitter:description', metadata.description);
    setMetaAttribute('name', 'twitter:image', socialImageUrl);

    setLinkRel('canonical', canonicalUrl);
    setJsonLd('site-structured-data', buildStructuredData(canonicalUrl));
  }, [pathname]);

  return null;
}

export default SEO;
